import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { fetchCatalogueService, syncServiceBranches } from "../services/serviceService";

/**
 * Which branches offer one service, and what each charges — the mirror of
 * picking services from a branch's own screen.
 *
 * A branch left unticked stops offering the service. The per-branch override
 * behaviour is unchanged; OUP-25 removed only the note that explained it.
 */
export default function ServiceBranchesModal({ service, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !service) return;

    setLoading(true);
    setErrors({});
    setBanner("");

    // OUP-24 — the offerings come from the service itself, not from the row the
    // catalogue list handed over. The list endpoint returns branches_count but
    // not the branches array, so trusting `service.branches` meant opening this
    // modal on a linked service with nothing ticked.
    let cancelled = false;

    Promise.all([fetchBranches(1, 100), fetchCatalogueService(service.id)])
      .then(([{ branches: list }, full]) => {
        if (cancelled) return;

        setBranches(list);

        // Start from what the service already says, so an existing price stays.
        const current = {};
        (full.branches ?? []).forEach((entry) => {
          current[entry.branch_id] = {
            offered: true,
            priceOverride: entry.price_override ?? "",
            isAvailableOnline: Boolean(entry.is_available_online),
            isActive: Boolean(entry.is_active),
          };
        });
        setRows(current);
      })
      .catch((err) => {
        if (!cancelled) setBanner(apiErrorMessage(err, t("catalogue.branchesLoadFailed")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service?.id, t]);

  const row = (branchId) =>
    rows[branchId] ?? { offered: false, priceOverride: "", isAvailableOnline: true, isActive: true };

  const update = (branchId, changes) =>
    setRows((current) => ({ ...current, [branchId]: { ...row(branchId), ...changes } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setBanner("");

    try {
      await syncServiceBranches(
        service.id,
        Object.entries(rows)
          .filter(([, value]) => value.offered)
          .map(([branchId, value]) => ({ branchId, ...value }))
      );

      onSaved();
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("catalogue.branchesSaveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !service) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-2xl rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} className="flex max-h-[85vh] flex-col" noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold">{t("catalogue.branchesTitle")}</h2>
              <p className="text-xs text-ink-2">{service.name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            {loading ? (
              <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
            ) : branches.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-2">{t("catalogue.noBranches")}</p>
            ) : (
              <ul className="divide-y divide-line">
                {branches.map((branch) => {
                  const current = row(branch.id);

                  return (
                    <li key={branch.id} className="flex flex-wrap items-center gap-3 py-3">
                      <label className="flex flex-1 items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={current.offered}
                          onChange={(e) => update(branch.id, { offered: e.target.checked })}
                          className="h-4 w-4 rounded border-line-strong text-accent-text"
                        />
                        <span>
                          {branch.name}
                          <span className="ms-2 text-xs text-ink-2">{branch.currency?.code}</span>
                        </span>
                      </label>

                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        dir="ltr"
                        disabled={!current.offered}
                        placeholder={service.price}
                        className="field-input w-32 disabled:bg-raised"
                        value={current.priceOverride}
                        onChange={(e) => update(branch.id, { priceOverride: e.target.value })}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            {Object.keys(errors).length > 0 && (
              <p className="field-error mt-2">{Object.values(errors)[0]?.[0]}</p>
            )}

          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || loading}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
